import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';

import { useAuth } from 'src/routes/context/AuthContext';
import { RequireRole } from 'src/routes/require-role';

import { varAlpha } from 'src/theme/styles';
import { AuthLayout } from 'src/layouts/auth';
import { DashboardLayout } from 'src/layouts/dashboard';

import { BranchView, BranchEditView, BranchDetailView } from 'src/sections/branch/view';
import { PermissionView } from 'src/sections/permission/view';
import { OvertimeView } from 'src/sections/overtime/view';
import { ReportsLayout, ReportsView, MonthlyAttendanceView, IrregularEmployeesView, RegularEmployeesView, SingleEmployeeView, BranchScorecardView } from 'src/sections/reports/view';
import { ProfileView } from 'src/sections/profile/profile-view';
import { SettingsView } from 'src/sections/settings/settings-view';
import BranchCreateView from 'src/sections/branch/view/branch-create-view';
import { BranchCreateEnhancedView } from 'src/sections/branch/view/branch-create-enhanced-view';
import PermissionEdit from 'src/sections/permission/detail/PermissionEdit';
import { EmployeeView, EmployeeEditView, EmployeeCreateView, EmployeeDetailView } from 'src/sections/employee/view';
import { CompanyView } from 'src/sections/company/view/company-view';
import { CompanyCreateView } from 'src/sections/company/view/company-create-view';
import { CompanyEditView } from 'src/sections/company/view/company-edit-view';
import PermissionCreate from 'src/sections/permission/detail/PermissionCreate';

// ----------------------------------------------------------------------

export const HomePage = lazy(() => import('src/pages/home'));
export const BlogPage = lazy(() => import('src/pages/blog'));
export const EmployeePage = lazy(() => import('src/pages/employee'));
// export const EmployeeTransferPage = lazy(() => import('src/pages/employee-transfer')); // Artık kullanılmıyor
export const SignInPage = lazy(() => import('src/pages/sign-in'));
export const ProductsPage = lazy(() => import('src/pages/products'));
export const Page404 = lazy(() => import('src/pages/page-not-found'));
export const BranchPage = lazy(() => import('src/pages/branch'));
export const SuspiciousLoginsPage = lazy(() => import('src/pages/suspicious-logins'));
export const SystemLogsPage = lazy(() => import('src/pages/system-logs'));
export const SecurityLogsPage = lazy(() => import('src/pages/security-logs'));
export const DeviceVersionsPage = lazy(() => import('src/pages/device-versions'));
export const BranchCreatePage = lazy(() => import('src/sections/branch/view/branch-create-view'));
export const ForgotPasswordPage = lazy(() => import('src/pages/forgot-password'));
export const ResetPasswordPage = lazy(() => import('src/pages/reset-password'));
export const AccountActivationPage = lazy(() => import('src/pages/account-activation'));
export const EmployeeSignupPage = lazy(() => import('src/pages/employee-signup'));

// ----------------------------------------------------------------------

const renderFallback = (
  <Box display="flex" alignItems="center" justifyContent="center" flex="1 1 auto">
    <LinearProgress
      sx={{
        width: 1,
        maxWidth: 320,
        bgcolor: (theme) => varAlpha(theme.vars.palette.text.primaryChannel, 0.16),
        [`& .${linearProgressClasses.bar}`]: { bgcolor: 'text.primary' },
      }}
    />
  </Box>
);

// Ortak bir layout wrapper component oluşturalım
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={renderFallback}>
    <RequireAuth>
      <DashboardLayout>
        {children}
      </DashboardLayout>
    </RequireAuth>
  </Suspense>
);

export function Router() {
  const { isAuthenticated } = useAuth();

  return useRoutes([
    {
      path: '/',
      element: isAuthenticated ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <Navigate to="/sign-in" replace />
      ),
    },
    {
      path: '/dashboard',
      element: (
        <DashboardLayout>
          <Suspense fallback={renderFallback}>
            <RequireAuth>
              <Outlet />
            </RequireAuth>
          </Suspense>
        </DashboardLayout>
      ),
      children: [
        { element: <HomePage />, index: true },
        { path: 'profile', element: <ProfileView /> },
        { path: 'settings', element: <SettingsView /> },
        { path: 'companies', element: <CompanyView /> },
        { path: 'companies/create', element: <CompanyCreateView /> },
        { path: 'companies/:id/edit', element: <CompanyEditView /> },
        { path: 'branches', element: <BranchView /> },
        { path: 'branches/create', element: <BranchCreateEnhancedView /> },
        { path: 'branches/:id/edit', element: <BranchEditView /> },
        { path: 'branches/:id', element: <BranchDetailView /> },
        { path: 'employee', element: <EmployeePage /> },
        { path: 'employee/create', element: <EmployeeCreateView /> },
        { path: 'employee/:userId', element: <EmployeeDetailView /> },
        { path: 'employee/:userId/edit', element: <EmployeeEditView /> },
        // { path: 'employee/transfer', element: <EmployeeTransferPage /> }, // Artık kullanılmıyor
        { path: 'permissions', element: <PermissionView /> },
        { path: 'permissions/new', element: <PermissionCreate /> },
        { path: 'permissions/:id', element: <PermissionEdit /> },
        { path: 'overtime', element: <OvertimeView /> },
        { path: 'suspicious-logins', element: <SuspiciousLoginsPage /> },
        {
          path: 'logs',
          element: (
            <RequireRole role="ROLE_SUPER_ADMIN">
              <SystemLogsPage />
            </RequireRole>
          ),
        },
        {
          path: 'security-logs',
          element: (
            <RequireRole role={['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']}>
              <SecurityLogsPage />
            </RequireRole>
          ),
        },
        {
          path: 'device-versions',
          element: (
            <RequireRole role={['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']}>
              <DeviceVersionsPage />
            </RequireRole>
          ),
        },
        {
          path: 'reports',
          element: <ReportsLayout />,
          children: [
            { index: true, element: <ReportsView /> },
            { path: 'monthly', element: <MonthlyAttendanceView /> },
            { path: 'irregular', element: <IrregularEmployeesView /> },
            { path: 'regular', element: <RegularEmployeesView /> },
            { path: 'employee', element: <SingleEmployeeView /> },
            { path: 'scorecard', element: <BranchScorecardView /> },
          ],
        },
      ],
    },
    {
      path: 'sign-in',
      element: (
        <AuthLayout>
          {isAuthenticated ? <Navigate to="/dashboard" replace /> : <SignInPage />}
        </AuthLayout>
      ),
    },
    {
      path: 'forgot-password',
      element: (
        <AuthLayout>
          {isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
        </AuthLayout>
      ),
    },
    {
      path: 'reset-password',
      element: (
        <AuthLayout>
          {isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />}
        </AuthLayout>
      ),
    },
    {
      path: 'hesap-aktivasyon',
      element: (
        <AuthLayout>
          {isAuthenticated ? <Navigate to="/dashboard" replace /> : <AccountActivationPage />}
        </AuthLayout>
      ),
    },
    {
      path: 'employee/signup',
      element: (
        <Suspense fallback={renderFallback}>
          <EmployeeSignupPage />
        </Suspense>
      ),
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return children;
}
